import { observable, computed, action, toJS } from 'mobx'
import { has } from 'lodash'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ActionStore from 'stores/action/ActionStore'
import RecordStore from 'stores/record/RecordStore'
import SessionStore from 'stores/session/SessionStore'
import ProjectStore from 'stores/project/ProjectStore'
import Record from 'stores/record/Record'
import Session from 'stores/session/Session'
import { IActionSchema } from 'stores/persistence/types'

import isActionValid from 'utils/isActionValid'

import { IActionCreatePayload, ActionType } from './types'

export const UNCOMITTED_RECORD_ID_PREFIX = `tmp-`

class Action extends BaseListItem {
  @observable id: string
  @observable recordId: string
  @observable sessionId: string
  @observable type: ActionType
  @observable value: any

  constructor(action: IActionCreatePayload & { id: string }) {
    super(action)

    this.id = action.id
    this.sessionId = action.sessionId
    this.type = action.type
    this.value = action.value

    if (this.type === 'create') {
      // A temporary ID that will be given to the temporary record that will be created.
      // This ID needs to be deterministic so IDB restores this action properly. However, it does not need to be dependent on the record's ID field value, because that will change after a commit anyway.
      const recordId = `${UNCOMITTED_RECORD_ID_PREFIX}-${this.id}`
      const modelId = action.value.modelId as string // type cast is okay because modelId will not be null `create` actions

      // Create the record
      RecordStore.add({
        id: recordId,
        modelId,
        value: {},
      })

      this.recordId = recordId
    } else {
      this.recordId = action.recordId! // `!` is fine here because a recordId is needed for update and delete actions
    }
  }

  @computed
  get record(): Record | null {
    if (!this.recordId) {
      return null
    }

    return RecordStore.get(this.recordId)
  }

  @computed
  get session(): Session | null {
    return SessionStore.get(this.sessionId)
  }

  @computed
  get isValid(): boolean {
    return isActionValid(this.id)
  }

  @action
  update(
    action: Partial<IActionCreatePayload>,
    options: IBaseListItemUpdateOptions = {}
  ) {
    has(action, 'recordId') && (this.recordId = action.recordId!)
    has(action, 'sessionId') && (this.sessionId = action.sessionId!)
    has(action, 'value') && (this.value = action.value)

    super.update(action, options)
  }

  serialize = (): IActionSchema => {
    return {
      projectId: ProjectStore.activeProjectId!,

      id: this.id,
      type: this.type,
      recordId: this.recordId,
      sessionId: this.sessionId,
      value: toJS(this.value),
    }
  }
}

export default Action
